import React from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import type { ProfileCardFrame as ProfileCardFrameModel } from './profileCardModel';
import { profileThumbSrc } from './profileThumbSrc';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Profile entry card; each part uses its own slice of it */
import styles from './ProfileEntryCard.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The CSS height of a frame (design: about 95 px at 1440 px). The image is asked at this height times its aspect. */
const FRAME_CSS_HEIGHT = 96;

interface ProfileCardFrameProps {
  frame: ProfileCardFrameModel | { aspect: number; round: boolean; content: { type: 'loading' } };
  /** The blurred cover of the newest release behind a ghost frame. */
  ghostSrc: string | null;
  label: string;
  onOpen?: () => void;
  testId?: string;
}

type FrameStyle = React.CSSProperties & { '--aspect': number };

/** One frame of a tile at the true aspect of its file (design profile-card a, `pf2`). A click opens its tool. */
const ProfileCardFrameComponent: React.FC<ProfileCardFrameProps> = ({ frame, ghostSrc, label, onOpen, testId }) => {
  const { content } = frame;
  const style: FrameStyle = { '--aspect': frame.aspect };
  let inner: React.ReactNode = null;
  switch (content.type) {
    case 'file': {
      const src = profileThumbSrc(content.asset, Math.round(FRAME_CSS_HEIGHT * frame.aspect));
      inner = src === null ? <span className={styles.placeholder} /> : <img src={src} alt="" loading="lazy" />;
      break;
    }
    case 'ghost':
      inner = (
        <span className={styles.placeholder}>
          {ghostSrc !== null && <img className={styles.ghost} src={ghostSrc} alt="" loading="lazy" />}
          <span className={styles.plus}>+</span>
        </span>
      );
      break;
    case 'placeholder':
      inner = (
        <span className={styles.placeholder}>
          <span className={styles.plus}>+</span>
        </span>
      );
      break;
    case 'making':
      inner = (
        <span className={styles.placeholder}>
          <span className={styles.making} />
        </span>
      );
      break;
    case 'bio':
      inner = <span className={styles.bio}>{content.text}</span>;
      break;
    case 'bioEmpty':
      inner = <span className={clsx(styles.bio, styles.bioEmpty)}>{label}</span>;
      break;
    case 'loading':
      inner = <span className={styles.loading} />;
      break;
    default: {
      const exhaustive: never = content;
      inner = exhaustive;
    }
  }
  if (onOpen === undefined) {
    return (
      <span className={clsx(styles.frame, frame.round && styles.frameRound)} style={style} data-testid={testId}>
        {inner}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={clsx(styles.frame, frame.round && styles.frameRound)}
      style={style}
      aria-label={label}
      onClick={onOpen}
      data-testid={testId}
      data-content={content.type}
    >
      {inner}
    </button>
  );
};

export const ProfileCardFrame = TypedMemo(ProfileCardFrameComponent);
